import useGetApi from '../useGetApi';
import useMutationApi from '../useMutationApi';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import config from 'src/config';
import ApiResponse from '../ApiResponse';
import ApiError from '../ApiError';
import { useToast } from 'src/context/ToastContext';
import type {
  PendingSpreadsheetUpload,
  ListPendingUploadsResponse,
  StartPendingUploadResponse,
} from '@erp/shared-types';

/**
 * Query key for pending uploads
 */
export const PENDING_UPLOADS_QUERY_KEY = ['pendingUploads'];

/**
 * Hook to list all pending spreadsheet uploads
 */
export function usePendingUploads(enabled = true) {
  return useGetApi<ListPendingUploadsResponse>({
    endpoint: 'spreadsheet/pending-uploads',
    queryKey: PENDING_UPLOADS_QUERY_KEY,
    options: {
      enabled,
      refetchInterval: 30000, // Refetch every 30 seconds to catch new uploads
    },
  });
}

/**
 * Hook to get a single pending upload with download URL
 */
export function usePendingUpload(id: string, enabled = true) {
  return useGetApi<PendingSpreadsheetUpload>({
    endpoint: `spreadsheet/pending-uploads/${id}`,
    queryKey: [...PENDING_UPLOADS_QUERY_KEY, id],
    options: {
      enabled: enabled && !!id,
    },
  });
}

/**
 * Hook to start processing a pending upload
 */
export function useStartPendingUpload() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<
    ApiResponse<StartPendingUploadResponse>,
    ApiError,
    { id: string }
  >({
    mutationFn: async ({ id }) => {
      const response = await fetch(
        `${config.apiBaseUrl}/spreadsheet/pending-uploads/${id}/start`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new ApiError(
          errorData.status || response.status,
          errorData.message || 'Failed to start pending upload',
        );
      }

      return (await response.json()) as ApiResponse<StartPendingUploadResponse>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PENDING_UPLOADS_QUERY_KEY });
    },
    onError: (error) => {
      showToast({
        message: error.message,
        title: 'Error',
        color: 'error',
        icon: 'error',
      });
    },
  });
}

interface UpdatePendingUploadRequest {
  id: string;
  status?: 'completed' | 'failed' | 'cancelled';
  resultAgreementId?: string;
  errorMessage?: string;
}

/**
 * Hook to update a pending upload status
 */
export function useUpdatePendingUpload() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<
    ApiResponse<{ id: string; status: string }>,
    ApiError,
    UpdatePendingUploadRequest
  >({
    mutationFn: async ({ id, status, resultAgreementId, errorMessage }) => {
      const response = await fetch(
        `${config.apiBaseUrl}/spreadsheet/pending-uploads/${id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            status,
            resultAgreementId,
            errorMessage,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new ApiError(
          errorData.status || response.status,
          errorData.message || 'Failed to update pending upload',
        );
      }

      return (await response.json()) as ApiResponse<{
        id: string;
        status: string;
      }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PENDING_UPLOADS_QUERY_KEY });
    },
    onError: (error) => {
      showToast({
        message: error.message,
        title: 'Error',
        color: 'error',
        icon: 'error',
      });
    },
  });
}

/**
 * Hook to delete a pending upload
 */
export function useDeletePendingUpload() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<
    ApiResponse<{ message: string }>,
    ApiError,
    { id: string }
  >({
    mutationFn: async ({ id }) => {
      const response = await fetch(
        `${config.apiBaseUrl}/spreadsheet/pending-uploads/${id}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new ApiError(
          errorData.status || response.status,
          errorData.message || 'Failed to delete pending upload',
        );
      }

      return (await response.json()) as ApiResponse<{ message: string }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PENDING_UPLOADS_QUERY_KEY });
      showToast({
        message: 'Pending upload deleted',
        title: 'Success',
        color: 'success',
        icon: 'check',
      });
    },
    onError: (error) => {
      showToast({
        message: error.message,
        title: 'Error',
        color: 'error',
        icon: 'error',
      });
    },
  });
}
